import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  api,
  type KnowledgeItemData,
} from "../lib/api";
import { useForegroundRefresh } from "../hooks/useForegroundRefresh";
import { AppError, logError, toUserMessage } from "../lib/errors";
import { getBackendMessage } from "../lib/executiveMemos";
import { saveGroupTalkDraft } from "../lib/groupTalkSessionStorage";
import { buildKnowledgeWallTalkDraft } from "../lib/knowledgeGroupTalkDraft";
import {
  canRetryKnowledgeItemAiPreparation,
  getKnowledgeAiPreparationFailureMessage,
  getKnowledgeFailureDisplayMessage,
  getKnowledgeItemDisplayName,
  getKnowledgeTypeLabel,
  getKnowledgeUnavailableReason,
  isKnowledgeItemGroupTalkUsable,
  isKnowledgeItemAiPreparationFailed,
  isKnowledgeItemPending,
  validateKnowledgePdfFile,
  validateKnowledgeUrlInput,
} from "../lib/knowledge";
import {
  startKnowledgeBackgroundTracking,
  stopKnowledgeBackgroundTracking,
  updateTrackedKnowledgeBackgroundItem,
} from "../lib/knowledgeBackground";
import { uploadKnowledgePdf } from "../lib/knowledgePdfUpload";
import { storage } from "../lib/storage";

const KNOWLEDGE_LIBRARY_TYPES = "pdf,executive_memo,url,company_research,deep_report";
const KNOWLEDGE_LIBRARY_LIMIT = 20;
const KNOWLEDGE_POLLING_INTERVAL_MS = 5000;
const KNOWLEDGE_HIDDEN_POLLING_INTERVAL_MS = 15000;
const MAX_URL_POLLING_ATTEMPTS = 60;

type ItemMutation = "renaming" | "reprocessing" | "retrying" | "syncing" | "deleting";
type Notification = { type: "success" | "error"; message: string };
type KnowledgeUrlOperation = "register" | "retry" | "rename" | "delete";
type KnowledgeLibraryReadOperation = "load" | "load_more" | "poll";
type KnowledgePollResult = {
  itemId: string;
  item: KnowledgeItemData | null;
  missing: boolean;
};

function replaceKnowledgeItem(
  items: KnowledgeItemData[],
  updatedItem: KnowledgeItemData,
  previousId = updatedItem.id
): KnowledgeItemData[] {
  const targetIndex = items.findIndex((item) => item.id === previousId || item.id === updatedItem.id);
  const withoutTarget = items.filter((item) => item.id !== previousId && item.id !== updatedItem.id);
  if (targetIndex < 0) return [updatedItem, ...withoutTarget];
  return [...withoutTarget.slice(0, targetIndex), updatedItem, ...withoutTarget.slice(targetIndex)];
}

function appendUniqueKnowledgeItems(current: KnowledgeItemData[], incoming: KnowledgeItemData[]): KnowledgeItemData[] {
  const knownIds = new Set(current.map((item) => item.id));
  return [...current, ...incoming.filter((item) => !knownIds.has(item.id))];
}

function mergeRefreshedKnowledgeItems(
  current: KnowledgeItemData[],
  incoming: KnowledgeItemData[],
  activeMutations: Record<string, ItemMutation>
): KnowledgeItemData[] {
  const currentById = new Map(current.map((item) => [item.id, item]));
  const protectedIncoming = incoming.map((item) => activeMutations[item.id] ? currentById.get(item.id) ?? item : item);
  const refreshedIds = new Set(protectedIncoming.map((item) => item.id));
  return [...protectedIncoming, ...current.filter((item) => !refreshedIds.has(item.id))];
}

function knowledgeMutationError(error: unknown, fallback: string, fields: string[] = []): string {
  return getBackendMessage(error, fields) || `${toUserMessage(error)} ${fallback}`;
}

function knowledgeUrlOperationError(error: unknown, operation: KnowledgeUrlOperation): string {
  const code = error instanceof AppError ? error.code : "UNKNOWN";
  if (operation === "register") {
    if (code === "CONFLICT") return "このURLは既にナレッジライブラリへ登録されています。";
    if (code === "BAD_REQUEST") return "URLを追加できませんでした。入力内容を確認して、もう一度お試しください。";
    if (code === "NETWORK" || code === "TIMEOUT") return "通信に失敗しました。ネットワーク状況を確認して、もう一度お試しください。";
    return "URLを追加できませんでした。入力内容を確認して、もう一度お試しください。";
  }
  if (operation === "retry") {
    if (code === "CONFLICT" || code === "BAD_REQUEST") return "このWebページは現在再試行できません。";
    return "再試行を開始できませんでした。時間をおいて、もう一度お試しください。";
  }
  if (operation === "rename") return "Webページの資料名を変更できませんでした。";
  return "Webページを削除できませんでした。時間をおいて、もう一度お試しください。";
}

function logKnowledgeUrlOperationError(error: unknown, operation: KnowledgeUrlOperation): void {
  const code = error instanceof AppError ? error.code : "UNKNOWN";
  const status = error instanceof AppError ? error.meta.status : undefined;
  logError(new Error(`knowledge_url_${operation}_failed`), {
    action: `knowledge_url_${operation}`,
    errorCode: code,
    statusClass: typeof status === "number" ? `${Math.floor(status / 100)}xx` : undefined,
  });
}

function logKnowledgeLibraryReadError(error: unknown, operation: KnowledgeLibraryReadOperation): void {
  const code = error instanceof AppError ? error.code : "UNKNOWN";
  const status = error instanceof AppError ? error.meta.status : undefined;
  logError(new Error(`knowledge_library_${operation}_failed`), {
    action: `knowledge_library_${operation}`,
    errorCode: code,
    statusClass: typeof status === "number" ? `${Math.floor(status / 100)}xx` : undefined,
  });
}

function hasKnowledgeTitleControlCharacter(value: string): boolean {
  return /\p{C}/u.test(value);
}

function normalizeKnowledgeTitle(value: string): { title: string; error: string } {
  if (hasKnowledgeTitleControlCharacter(value)) {
    return { title: value, error: "資料名に改行、タブ、制御文字は使用できません。" };
  }
  const title = value.trim();
  if (!title) return { title, error: "資料名を入力してください。" };
  if (Array.from(title).length > 255) return { title, error: "資料名は255文字以内で入力してください。" };
  return { title, error: "" };
}

function getKnowledgeItemAiState(item: KnowledgeItemData): {
  label: string;
  detail?: string;
  tone: "ready" | "pending" | "failed";
} {
  if (isKnowledgeItemGroupTalkUsable(item)) {
    return { label: "AIで利用できます", tone: "ready" };
  }
  if (isKnowledgeItemAiPreparationFailed(item)) {
    return {
      label: "確認が必要です",
      detail: getKnowledgeAiPreparationFailureMessage(item.ai_preparation_failure_code),
      tone: "failed",
    };
  }
  if (item.status === "failed") {
    return { label: "確認が必要です", detail: getKnowledgeFailureDisplayMessage(item), tone: "failed" };
  }
  if (item.status === "uploading") {
    return { label: "アップロード中", tone: "pending" };
  }
  if (isKnowledgeItemPending(item)) {
    return { label: "準備しています", tone: "pending" };
  }
  return {
    label: "確認が必要です",
    detail: getKnowledgeUnavailableReason(item) || "この資料はAIでは利用できません。",
    tone: "failed",
  };
}

function KnowledgeItemCard({
  item,
  mutation,
  pollingTimedOut,
  onStartWallTalk,
  onRequestRename,
  onRequestReprocess,
  onRetryUrl,
  onRequestDelete,
  onRefreshFromExecutiveMemo,
}: {
  item: KnowledgeItemData;
  mutation?: ItemMutation;
  pollingTimedOut?: boolean;
  onStartWallTalk: (item: KnowledgeItemData) => void;
  onRequestRename: (item: KnowledgeItemData) => void;
  onRequestReprocess: (item: KnowledgeItemData) => void;
  onRetryUrl: (item: KnowledgeItemData) => void;
  onRequestDelete: (item: KnowledgeItemData) => void;
  onRefreshFromExecutiveMemo: (item: KnowledgeItemData) => void;
}) {
  const displayName = getKnowledgeItemDisplayName(item);
  const groupTalkUsable = isKnowledgeItemGroupTalkUsable(item);
  const aiState = getKnowledgeItemAiState(item);
  const mutating = Boolean(mutation);
  const canReprocessPdf = item.type === "pdf" && item.status === "failed" && item.can_retry;
  const canRetryUrl = item.type === "url" && item.status === "failed" && item.can_retry;
  const canRetryAiPreparation = canRetryKnowledgeItemAiPreparation(item);
  const canRefreshFromExecutiveMemo = item.type === "executive_memo" && item.status === "failed" && Boolean(item.source_id);

  return (
    <article className="max-w-full overflow-hidden rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 shadow-sm">
      <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="shrink-0 rounded-full bg-[#FFF3E9] px-3 py-1 text-[11px] font-black text-[#A46D4C]">
              {getKnowledgeTypeLabel(item.type)}
            </span>
            <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-black ${
              aiState.tone === "ready"
                ? "bg-[#EDF7EA] text-[#4B7949]"
                : aiState.tone === "failed"
                  ? "bg-[#FFF0EB] text-[#A45645]"
                  : "bg-[#F5EEE7] text-[#7C5A4E]"
            }`}>
              {aiState.label}
            </span>
          </div>
          <h2 className="mt-3 max-w-full break-words text-xl font-black text-[#5D4D46] [overflow-wrap:anywhere]">
            {displayName}
          </h2>
          {aiState.detail ? (
            <div className="mt-4 max-w-full rounded-[1rem] border border-[#F0C8BA] bg-[#FFF7F1] px-3 py-2">
              <p className="mt-1 break-words text-sm font-bold text-[#C06D5A] [overflow-wrap:anywhere]">
                {aiState.detail}
              </p>
            </div>
          ) : null}
          {item.type === "url" && pollingTimedOut && isKnowledgeItemPending(item) ? (
            <p className="mt-4 break-words text-sm font-bold text-[#8F7468] [overflow-wrap:anywhere]">
              準備に時間がかかっています。しばらくしてから画面を再読み込みして状態を確認してください。
            </p>
          ) : null}
        </div>
        <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto sm:max-w-[18rem] sm:justify-end">
          <Link
            to={`/request/knowledge/${encodeURIComponent(item.id)}`}
            className="inline-flex min-h-[44px] max-w-full items-center justify-center whitespace-normal rounded-full border border-[#D9C5B9] bg-white px-5 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1]"
          >
            詳細
          </Link>
          {groupTalkUsable ? (
            <button
              type="button"
              aria-label={`この資料で壁打ち ${displayName}`}
              onClick={() => onStartWallTalk(item)}
              disabled={!groupTalkUsable || mutating}
              className="min-h-[44px] max-w-full whitespace-normal rounded-full bg-[#5D4D46] px-5 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:bg-[#B9AAA2] disabled:opacity-70"
            >
              この資料で壁打ち
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onRequestRename(item)}
            disabled={mutating}
            className="min-h-[36px] max-w-full whitespace-normal rounded-full px-3 py-1 text-xs font-bold text-[#7C6A62] underline decoration-[#D9C5B9] underline-offset-4 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation === "renaming" ? "保存中..." : "名前を変更"}
          </button>
          {canReprocessPdf ? (
            <button
              type="button"
              onClick={() => onRequestReprocess(item)}
              disabled={mutating}
              className="min-h-[36px] max-w-full whitespace-normal rounded-full px-3 py-1 text-xs font-bold text-[#7C6A62] underline decoration-[#D9C5B9] underline-offset-4 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation === "reprocessing" ? "開始中..." : "PDFをもう一度処理"}
            </button>
          ) : null}
          {canRetryUrl || canRetryAiPreparation ? (
            <button
              type="button"
              onClick={() => onRetryUrl(item)}
              disabled={mutating}
              className="min-h-[36px] max-w-full whitespace-normal rounded-full px-3 py-1 text-xs font-bold text-[#7C6A62] underline decoration-[#D9C5B9] underline-offset-4 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation === "retrying" ? "開始中..." : canRetryAiPreparation ? "要約を再試行" : "再試行"}
            </button>
          ) : null}
          {canRefreshFromExecutiveMemo ? (
            <button
              type="button"
              onClick={() => onRefreshFromExecutiveMemo(item)}
              disabled={mutating}
              className="min-h-[36px] max-w-full whitespace-normal rounded-full px-3 py-1 text-xs font-bold text-[#7C6A62] underline decoration-[#D9C5B9] underline-offset-4 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation === "syncing" ? "更新中..." : "社長メモから更新"}
            </button>
          ) : null}
          <button
            type="button"
            aria-label={`ナレッジを削除 ${displayName}`}
            onClick={() => onRequestDelete(item)}
            disabled={mutating}
            className="min-h-[36px] max-w-full whitespace-normal rounded-full px-3 py-1 text-xs font-bold text-[#A45645] underline decoration-[#E2B9A9] underline-offset-4 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation === "deleting" ? "削除中..." : "削除"}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function KnowledgeLibraryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<KnowledgeItemData[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [urlInput, setUrlInput] = useState("");
  const [urlError, setUrlError] = useState("");
  const [urlSubmitting, setUrlSubmitting] = useState(false);
  const [urlFormOpen, setUrlFormOpen] = useState(false);
  const urlSubmissionRef = useRef(false);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [mutations, setMutations] = useState<Record<string, ItemMutation>>({});
  const mutationRef = useRef<Record<string, ItemMutation>>({});
  const itemRevisionRef = useRef<Record<string, number>>({});
  const urlPollingAttemptRef = useRef<Record<string, number>>({});
  const [timedOutUrlItemIds, setTimedOutUrlItemIds] = useState<Record<string, true>>({});
  const loadedAdditionalPagesRef = useRef(false);
  const [renameTarget, setRenameTarget] = useState<KnowledgeItemData | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [renameError, setRenameError] = useState("");
  const [reprocessTarget, setReprocessTarget] = useState<KnowledgeItemData | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeItemData | null>(null);

  const pendingItemKey = useMemo(
    () => items
      .filter((item) => isKnowledgeItemPending(item) && !(item.type === "url" && timedOutUrlItemIds[item.id]))
      .map((item) => item.id)
      .sort()
      .join("\u001f"),
    [items, timedOutUrlItemIds]
  );
  const pendingUrlItemKey = useMemo(
    () => items
      .filter((item) => item.type === "url" && isKnowledgeItemPending(item) && !timedOutUrlItemIds[item.id])
      .map((item) => item.id)
      .sort()
      .join("\u001f"),
    [items, timedOutUrlItemIds]
  );
  const clearUrlPollingState = useCallback((itemIds: readonly string[]) => {
    if (itemIds.length === 0) return;
    itemIds.forEach((itemId) => {
      delete urlPollingAttemptRef.current[itemId];
    });
    setTimedOutUrlItemIds((current) => {
      const next = { ...current };
      let changed = false;
      itemIds.forEach((itemId) => {
        if (!next[itemId]) return;
        delete next[itemId];
        changed = true;
      });
      return changed ? next : current;
    });
  }, []);

  const markUrlPollingTimedOut = useCallback((itemIds: readonly string[]) => {
    if (itemIds.length === 0) return;
    setTimedOutUrlItemIds((current) => {
      const next = { ...current };
      let changed = false;
      itemIds.forEach((itemId) => {
        if (next[itemId]) return;
        next[itemId] = true;
        changed = true;
      });
      return changed ? next : current;
    });
  }, []);

  const showNotification = (type: Notification["type"], message: string) => {
    setNotification({ type, message });
  };

  const incrementItemRevision = (itemId: string) => {
    itemRevisionRef.current[itemId] = (itemRevisionRef.current[itemId] ?? 0) + 1;
  };

  const startMutation = (itemId: string, mutation: ItemMutation): boolean => {
    if (mutationRef.current[itemId]) return false;
    incrementItemRevision(itemId);
    mutationRef.current = { ...mutationRef.current, [itemId]: mutation };
    setMutations(mutationRef.current);
    return true;
  };

  const finishMutation = (itemId: string, mutation: ItemMutation) => {
    if (mutationRef.current[itemId] !== mutation) return;
    incrementItemRevision(itemId);
    const next = { ...mutationRef.current };
    delete next[itemId];
    mutationRef.current = next;
    setMutations(next);
  };

  const refreshFirstPage = useCallback(async (): Promise<void> => {
    const response = await api.listKnowledgeItems({
      type: KNOWLEDGE_LIBRARY_TYPES,
      limit: KNOWLEDGE_LIBRARY_LIMIT,
    });
    setItems((current) => mergeRefreshedKnowledgeItems(current, response.items, mutationRef.current));
    if (!loadedAdditionalPagesRef.current) setNextCursor(response.next_cursor ?? null);
  }, []);

  useForegroundRefresh(async () => {
    if (loading || loadingMore || pdfUploading || urlSubmitting) return;
    try {
      await refreshFirstPage();
    } catch (error) {
      logKnowledgeLibraryReadError(error, "load");
    }
  }, { enabled: !loading });

  const submitUrl = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (urlSubmissionRef.current) return;
    const validation = validateKnowledgeUrlInput(urlInput);
    if (validation.error) {
      setUrlError(validation.error);
      return;
    }

    urlSubmissionRef.current = true;
    setUrlSubmitting(true);
    setUrlError("");
    try {
      const createdItem = await api.createKnowledgeItemFromUrl(validation.url);
      startKnowledgeBackgroundTracking(storage.getAuth().userId, createdItem);
      clearUrlPollingState([createdItem.id]);
      setItems((current) => replaceKnowledgeItem(current, createdItem));
      setUrlInput("");
      showNotification("success", "URLを追加しました。内容を読み取っています。");
      try {
        await refreshFirstPage();
      } catch (refreshError) {
        logKnowledgeUrlOperationError(refreshError, "register");
      }
    } catch (error) {
      logKnowledgeUrlOperationError(error, "register");
      showNotification("error", knowledgeUrlOperationError(error, "register"));
    } finally {
      urlSubmissionRef.current = false;
      setUrlSubmitting(false);
    }
  };

  useEffect(() => {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    setLoading(true);
    setNotification(null);
    loadedAdditionalPagesRef.current = false;

    api.listKnowledgeItems(
      { type: KNOWLEDGE_LIBRARY_TYPES, limit: KNOWLEDGE_LIBRARY_LIMIT },
      controller?.signal
    )
      .then((response) => {
        setItems(response.items);
        response.items.forEach((item) => {
          updateTrackedKnowledgeBackgroundItem(storage.getAuth().userId, item);
        });
        setNextCursor(response.next_cursor ?? null);
      })
      .catch((error) => {
        if (controller?.signal.aborted) return;
        logKnowledgeLibraryReadError(error, "load");
        showNotification("error", "ナレッジライブラリを読み込めませんでした。時間をおいて、もう一度お試しください。");
      })
      .finally(() => {
        if (!controller?.signal.aborted) setLoading(false);
      });

    return () => controller?.abort();
  }, []);

  useEffect(() => {
    if (!notification) return;
    const timeout = window.setTimeout(
      () => setNotification(null),
      notification.type === "success" ? 5000 : 8000
    );
    return () => window.clearTimeout(timeout);
  }, [notification]);

  useEffect(() => {
    const activePendingUrlIds = new Set(
      items
        .filter((item) => item.type === "url" && isKnowledgeItemPending(item))
        .map((item) => item.id)
    );
    const staleItemIds = Object.keys(urlPollingAttemptRef.current)
      .filter((itemId) => !activePendingUrlIds.has(itemId));
    if (staleItemIds.length > 0) clearUrlPollingState(staleItemIds);
  }, [clearUrlPollingState, items]);

  useEffect(() => {
    if (!pendingItemKey) return;
    const itemIds = pendingItemKey.split("\u001f");
    const urlItemIds = new Set(pendingUrlItemKey ? pendingUrlItemKey.split("\u001f") : []);
    let stopped = false;
    let timeout: number | null = null;
    let controller: AbortController | null = null;

    const scheduleNextPoll = () => {
      if (stopped) return;
      const interval = typeof document !== "undefined" && document.visibilityState === "hidden"
        ? KNOWLEDGE_HIDDEN_POLLING_INTERVAL_MS
        : KNOWLEDGE_POLLING_INTERVAL_MS;
      timeout = window.setTimeout(() => void poll(), interval);
    };

    const poll = async () => {
      if (stopped) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        scheduleNextPoll();
        return;
      }

      const alreadyCappedIds: string[] = [];
      const requestItemIds = itemIds.filter((itemId) => {
        if (!urlItemIds.has(itemId)) return true;
        const attempts = urlPollingAttemptRef.current[itemId] ?? 0;
        if (attempts >= MAX_URL_POLLING_ATTEMPTS) {
          alreadyCappedIds.push(itemId);
          return false;
        }
        urlPollingAttemptRef.current[itemId] = attempts + 1;
        return true;
      });
      markUrlPollingTimedOut(alreadyCappedIds);
      if (requestItemIds.length === 0) return;

      controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      const revisionSnapshot: Record<string, number> = {};
      requestItemIds.forEach((itemId) => {
        revisionSnapshot[itemId] = itemRevisionRef.current[itemId] ?? 0;
      });
      const pollResults: KnowledgePollResult[] = await Promise.all(requestItemIds.map(async (itemId) => {
        try {
          return {
            itemId,
            item: await api.getKnowledgeItem(itemId, controller?.signal),
            missing: false,
          };
        } catch (error) {
          const missing = error instanceof AppError && error.code === "NOT_FOUND";
          if (!controller?.signal.aborted && !missing) logKnowledgeLibraryReadError(error, "poll");
          return { itemId, item: null, missing };
        }
      }));
      if (stopped) return;

      const updates = new Map<string, KnowledgeItemData>();
      const removedItemIds = new Set<string>();
      const completedItemIds: string[] = [];
      const timedOutItemIds: string[] = [];

      pollResults.forEach((result) => {
        const revisionUnchanged = (itemRevisionRef.current[result.itemId] ?? 0)
          === revisionSnapshot[result.itemId];
        if (!revisionUnchanged || mutationRef.current[result.itemId]) {
          return;
        }

        if (result.missing) {
          stopKnowledgeBackgroundTracking(storage.getAuth().userId, result.itemId);
          removedItemIds.add(result.itemId);
          completedItemIds.push(result.itemId);
          return;
        }

        if (result.item) {
          updates.set(result.itemId, result.item);
          updateTrackedKnowledgeBackgroundItem(storage.getAuth().userId, result.item);
          if (urlItemIds.has(result.itemId) && !isKnowledgeItemPending(result.item)) {
            completedItemIds.push(result.itemId);
            return;
          }
        }

        if (urlItemIds.has(result.itemId)
          && (urlPollingAttemptRef.current[result.itemId] ?? 0) >= MAX_URL_POLLING_ATTEMPTS) {
          timedOutItemIds.push(result.itemId);
        }
      });

      setItems((current) => current
        .filter((item) => !removedItemIds.has(item.id))
        .map((item) => updates.get(item.id) ?? item));
      clearUrlPollingState(completedItemIds);
      markUrlPollingTimedOut(timedOutItemIds);
      scheduleNextPoll();
    };

    scheduleNextPoll();
    return () => {
      stopped = true;
      if (timeout !== null) window.clearTimeout(timeout);
      controller?.abort();
    };
  }, [
    clearUrlPollingState,
    markUrlPollingTimedOut,
    pendingItemKey,
    pendingUrlItemKey,
  ]);

  const startWallTalk = (item: KnowledgeItemData) => {
    if (!isKnowledgeItemGroupTalkUsable(item) || mutationRef.current[item.id]) return;
    const draft = buildKnowledgeWallTalkDraft(item);
    saveGroupTalkDraft(draft);
    navigate("/talk/group", { state: { draft } });
  };

  const uploadPdf = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null;
    event.currentTarget.value = "";
    if (!file || pdfUploading) return;
    const validationError = validateKnowledgePdfFile(file);
    if (validationError) {
      showNotification("error", validationError);
      return;
    }

    setPdfUploading(true);
    try {
      await uploadKnowledgePdf(file, {
        userId: storage.getAuth().userId,
        onItemChange: (item) => {
          setItems((current) => replaceKnowledgeItem(current, item));
        },
      });
      showNotification("success", "PDFを追加しました。AIで使うための準備を進めています。");
    } catch (error) {
      logError(error);
      showNotification("error", `${toUserMessage(error)} もう一度アップロードできます。`);
    } finally {
      setPdfUploading(false);
    }
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    const cursor = nextCursor;
    setLoadingMore(true);
    try {
      const response = await api.listKnowledgeItems({
        type: KNOWLEDGE_LIBRARY_TYPES,
        limit: KNOWLEDGE_LIBRARY_LIMIT,
        cursor,
      });
      setItems((current) => appendUniqueKnowledgeItems(current, response.items));
      setNextCursor(response.next_cursor ?? null);
      loadedAdditionalPagesRef.current = true;
    } catch (error) {
      logKnowledgeLibraryReadError(error, "load_more");
      showNotification("error", "続きを読み込めませんでした。時間をおいて、もう一度お試しください。");
    } finally {
      setLoadingMore(false);
    }
  };

  const openRenameModal = (item: KnowledgeItemData) => {
    if (mutationRef.current[item.id]) return;
    setRenameTarget(item);
    setRenameTitle(getKnowledgeItemDisplayName(item));
    setRenameError("");
  };

  const saveRename = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!renameTarget) return;
    const validation = normalizeKnowledgeTitle(renameTitle);
    if (validation.error) {
      setRenameError(validation.error);
      return;
    }
    if (validation.title === getKnowledgeItemDisplayName(renameTarget)) {
      setRenameError("現在と同じ資料名です。");
      return;
    }
    if (!startMutation(renameTarget.id, "renaming")) return;
    setRenameError("");

    try {
      const updatedItem = await api.updateKnowledgeItemTitle(renameTarget.id, validation.title);
      setItems((current) => replaceKnowledgeItem(current, updatedItem, renameTarget.id));
      setRenameTarget(null);
      showNotification("success", "資料名を変更しました。");
    } catch (error) {
      if (renameTarget.type === "url") {
        logKnowledgeUrlOperationError(error, "rename");
        setRenameError(knowledgeUrlOperationError(error, "rename"));
      } else {
        logError(error);
        setRenameError(knowledgeMutationError(error, "資料名を変更できませんでした。", ["title"]));
      }
    } finally {
      finishMutation(renameTarget.id, "renaming");
    }
  };

  const reprocessPdf = async () => {
    if (!reprocessTarget || !startMutation(reprocessTarget.id, "reprocessing")) return;
    const target = reprocessTarget;
    try {
      const updatedItem = await api.reprocessKnowledgePdf(target.id);
      startKnowledgeBackgroundTracking(storage.getAuth().userId, updatedItem);
      setItems((current) => replaceKnowledgeItem(current, updatedItem, target.id));
      setReprocessTarget(null);
      showNotification("success", "PDFの再処理を開始しました。");
    } catch (error) {
      logError(error);
      setReprocessTarget(null);
      showNotification("error", knowledgeMutationError(error, "PDFをもう一度処理できませんでした。"));
    } finally {
      finishMutation(target.id, "reprocessing");
    }
  };

  const retryUrl = async (item: KnowledgeItemData) => {
    const retryAiPreparation = canRetryKnowledgeItemAiPreparation(item);
    const retryFullUrl = item.type === "url" && item.status === "failed" && item.can_retry;
    if ((!retryAiPreparation && !retryFullUrl) || !startMutation(item.id, "retrying")) return;
    try {
      const updatedItem = await api.retryKnowledgeUrl(item.id);
      startKnowledgeBackgroundTracking(storage.getAuth().userId, updatedItem);
      clearUrlPollingState([item.id]);
      setItems((current) => replaceKnowledgeItem(current, updatedItem, item.id));
      showNotification(
        "success",
        retryAiPreparation ? "要約の再試行を開始しました。" : "再試行を開始しました。"
      );
      if (!retryAiPreparation) {
        try {
          await refreshFirstPage();
        } catch (refreshError) {
          logKnowledgeUrlOperationError(refreshError, "retry");
        }
      }
    } catch (error) {
      logKnowledgeUrlOperationError(error, "retry");
      showNotification("error", knowledgeUrlOperationError(error, "retry"));
    } finally {
      finishMutation(item.id, "retrying");
    }
  };

  const refreshFromExecutiveMemo = async (item: KnowledgeItemData) => {
    if (item.type !== "executive_memo" || !item.source_id || !startMutation(item.id, "syncing")) return;
    try {
      const updatedItem = await api.createKnowledgeItemFromExecutiveMemo(item.source_id);
      startKnowledgeBackgroundTracking(storage.getAuth().userId, updatedItem);
      setItems((current) => replaceKnowledgeItem(current, updatedItem, item.id));
      showNotification("success", "社長メモの内容を更新しました。");
    } catch (error) {
      logError(error);
      showNotification("error", knowledgeMutationError(error, "社長メモから更新できませんでした。"));
    } finally {
      finishMutation(item.id, "syncing");
    }
  };

  const deleteKnowledge = async () => {
    if (!deleteTarget || !startMutation(deleteTarget.id, "deleting")) return;
    const target = deleteTarget;
    try {
      await api.deleteKnowledgeItem(target.id);
      stopKnowledgeBackgroundTracking(storage.getAuth().userId, target.id);
      clearUrlPollingState([target.id]);
      setItems((current) => current.filter((item) => item.id !== target.id));
      setDeleteTarget(null);
      showNotification("success", "資料を削除しました。");
    } catch (error) {
      if (target.type === "url") {
        logKnowledgeUrlOperationError(error, "delete");
        showNotification("error", knowledgeUrlOperationError(error, "delete"));
      } else {
        logError(error);
        showNotification("error", knowledgeMutationError(error, "ナレッジを削除できませんでした。"));
      }
    } finally {
      finishMutation(target.id, "deleting");
    }
  };

  const renameSaving = Boolean(renameTarget && mutations[renameTarget.id] === "renaming");
  const reprocessStarting = Boolean(reprocessTarget && mutations[reprocessTarget.id] === "reprocessing");
  const deletePending = Boolean(deleteTarget && mutations[deleteTarget.id] === "deleting");

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-5xl">
        <section className="border-b border-[#EADBC8] pb-6">
          <p className="text-xs font-black tracking-[0.18em] text-[#A67C52]">ナレッジライブラリ</p>
          <h1 className="mt-3 break-words text-3xl font-black text-[#5D4D46] sm:text-4xl">資料をAIで使う</h1>
          <p className="mt-3 max-w-2xl break-words text-sm leading-7 text-[#6B5851]">
            PDFやWebページ、社長メモ、企業リサーチ結果、深掘りレポートを登録すると、AI壁打ちなどで繰り返し使えます。
          </p>
          <h2 className="mt-7 text-xl font-black text-[#5D4D46]">資料を追加する</h2>
          <div aria-label="資料を追加する" className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div className="min-w-0 rounded-[1rem] border border-[#EADBC8] bg-white px-4 py-4">
              <p className="font-black text-[#5D4D46]">PDF</p>
              <p className="mt-1 leading-6 text-[#6B5851]">会社案内・提案書など</p>
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only"
                aria-label="PDFファイルを選択"
                onChange={(event) => void uploadPdf(event)}
              />
              <button
                type="button"
                onClick={() => pdfInputRef.current?.click()}
                disabled={pdfUploading}
                className="mt-3 min-h-[40px] rounded-full bg-[#5D4D46] px-4 py-2 text-xs font-black text-white disabled:opacity-60"
              >
                {pdfUploading ? "追加中..." : "PDFを追加"}
              </button>
            </div>
            <div className="min-w-0 rounded-[1rem] border border-[#EADBC8] bg-white px-4 py-4">
              <p className="font-black text-[#5D4D46]">Webページ</p>
              <p className="mt-1 leading-6 text-[#6B5851]">参考にしたいWebページ</p>
              <button
                type="button"
                aria-expanded={urlFormOpen}
                aria-controls="knowledge-url-form"
                onClick={() => setUrlFormOpen((current) => !current)}
                className="mt-3 min-h-[40px] rounded-full bg-[#5D4D46] px-4 py-2 text-xs font-black text-white"
              >
                {urlFormOpen ? "入力欄を閉じる" : "URLを追加"}
              </button>
            </div>
            <div className="min-w-0 rounded-[1rem] border border-[#EADBC8] bg-white px-4 py-4">
              <p className="font-black text-[#5D4D46]">社長メモ</p>
              <p className="mt-1 leading-6 text-[#6B5851]">紙資料・パンフレット・手書きメモなど</p>
              <Link
                to="/request/executive-memos"
                state={{ returnTo: "/request/knowledge" }}
                className="mt-3 inline-flex min-h-[40px] items-center rounded-full border border-[#D9C5B9] px-4 py-2 text-xs font-black text-[#6B5851]"
              >
                社長メモを開く
              </Link>
            </div>
            <div className="min-w-0 rounded-[1rem] border border-[#EADBC8] bg-white px-4 py-4">
              <p className="font-black text-[#5D4D46]">企業リサーチ</p>
              <p className="mt-1 leading-6 text-[#6B5851]">調べた企業情報を資料として残す</p>
              <Link to="/request/company-research" className="mt-3 inline-flex min-h-[40px] items-center rounded-full border border-[#D9C5B9] px-4 py-2 text-xs font-black text-[#6B5851]">
                企業リサーチを開く
              </Link>
            </div>
            <div className="min-w-0 rounded-[1rem] border border-[#EADBC8] bg-white px-4 py-4">
              <p className="font-black text-[#5D4D46]">深掘りレポート</p>
              <p className="mt-1 leading-6 text-[#6B5851]">作成した深掘りレポートをAIで使える資料として再利用する</p>
              <Link to="/reports" className="mt-3 inline-flex min-h-[40px] items-center rounded-full bg-[#5D4D46] px-4 py-2 text-xs font-black text-white">
                深掘りレポートを見る
              </Link>
            </div>
          </div>
          {urlFormOpen ? (
            <form id="knowledge-url-form" onSubmit={(event) => void submitUrl(event)} className="mt-4 min-w-0 rounded-[1rem] border border-[#EADBC8] bg-white p-4" aria-busy={urlSubmitting}>
              <label htmlFor="knowledge-url-input" className="block text-sm font-black text-[#6B5851]">WebページのURL</label>
              <p id="knowledge-url-help" className="mt-1 break-words text-xs leading-5 text-[#7C6A62]">
                公開されているWebページを読み取り、AIで使える資料にします。
              </p>
              <div className="mt-3 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-stretch">
                <input
                  id="knowledge-url-input"
                  type="text"
                  inputMode="url"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="https://example.com/article"
                  value={urlInput}
                  onChange={(event) => { setUrlInput(event.target.value); setUrlError(""); }}
                  onPaste={(event) => {
                    const validation = validateKnowledgeUrlInput(event.clipboardData.getData("text"));
                    if (!validation.error || !/制御文字/.test(validation.error)) return;
                    event.preventDefault();
                    setUrlError(validation.error);
                  }}
                  disabled={urlSubmitting}
                  aria-invalid={urlError ? "true" : "false"}
                  aria-describedby={urlError ? "knowledge-url-help knowledge-url-error" : "knowledge-url-help"}
                  className="min-h-[44px] min-w-0 flex-1 rounded-xl border border-[#D9C5B9] px-3 py-2 text-base text-[#4A3E39] outline-none focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6] disabled:bg-[#F7F2EE]"
                />
                <button type="submit" disabled={urlSubmitting} className="min-h-[44px] w-full shrink-0 rounded-full bg-[#5D4D46] px-6 py-2 text-sm font-black text-white disabled:opacity-60 sm:w-auto">
                  {urlSubmitting ? "追加中..." : "URLを追加"}
                </button>
              </div>
              {urlError ? <p id="knowledge-url-error" role="alert" className="mt-3 rounded-xl bg-[#FFF0EB] px-3 py-2 text-sm font-bold text-[#B4533A]">{urlError}</p> : null}
            </form>
          ) : null}

        </section>

        {notification ? (
          <div
            role={notification.type === "error" ? "alert" : "status"}
            className={`mt-5 flex max-w-full items-start justify-between gap-3 rounded-[1rem] border px-4 py-3 text-sm font-bold ${
              notification.type === "error"
                ? "border-[#F0C8BA] bg-[#FFF7F1] text-[#C06D5A]"
                : "border-[#BFD8C0] bg-[#F5FBF4] text-[#4B7949]"
            }`}
          >
            <span className="min-w-0 break-words [overflow-wrap:anywhere]">{notification.message}</span>
            <button
              type="button"
              aria-label="通知を閉じる"
              onClick={() => setNotification(null)}
              className="shrink-0 rounded-full px-2 py-0.5 text-base leading-none hover:bg-black/5"
            >
              ×
            </button>
          </div>
        ) : null}

        <section className="mt-6">
          <h2 className="mb-4 text-xl font-black text-[#5D4D46]">登録済みの資料</h2>
          {loading ? (
            <p className="rounded-[1.2rem] border border-[#EADBC8] bg-white px-4 py-4 text-sm font-bold text-[#8F7468]">
              ナレッジを読み込み中です...
            </p>
          ) : items.length === 0 ? (
            <div className="rounded-[1.4rem] border border-dashed border-[#D9C5B9] bg-white px-5 py-6">
              <h3 className="break-words text-lg font-black text-[#5D4D46]">まだ資料がありません</h3>
              <p className="mt-2 break-words text-sm leading-6 text-[#6B5851]">
                上の「資料を追加する」から登録すると、WizMateが読み取り、AI壁打ちなどで使えるようになります。
              </p>
            </div>
          ) : (
            <div className="grid min-w-0 gap-4">
              {items.map((item) => (
                <KnowledgeItemCard
                  key={item.id}
                  item={item}
                  mutation={mutations[item.id]}
                  pollingTimedOut={Boolean(timedOutUrlItemIds[item.id])}
                  onStartWallTalk={startWallTalk}
                  onRequestRename={openRenameModal}
                  onRequestReprocess={setReprocessTarget}
                  onRetryUrl={(target) => void retryUrl(target)}
                  onRequestDelete={setDeleteTarget}
                  onRefreshFromExecutiveMemo={(target) => void refreshFromExecutiveMemo(target)}
                />
              ))}
            </div>
          )}
          {nextCursor ? (
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="min-h-[44px] max-w-full whitespace-normal rounded-full border border-[#D9C5B9] bg-white px-6 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingMore ? "読み込み中..." : "さらに読み込む"}
              </button>
            </div>
          ) : null}
        </section>
      </div>

      {renameTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#2F2521]/50 px-3 py-5 sm:px-4">
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-rename-title"
            onSubmit={(event) => void saveRename(event)}
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[1.4rem] bg-white p-5 shadow-xl"
          >
            <h2 id="knowledge-rename-title" className="break-words text-lg font-black text-[#5D4D46]">資料名を変更</h2>
            <label htmlFor="knowledge-rename-input" className="mt-4 block text-sm font-black text-[#6B5851]">資料名</label>
            <input
              id="knowledge-rename-input"
              type="text"
              value={renameTitle}
              onChange={(event) => {
                setRenameTitle(event.target.value);
                setRenameError("");
              }}
              onPaste={(event) => {
                if (!hasKnowledgeTitleControlCharacter(event.clipboardData.getData("text"))) return;
                event.preventDefault();
                setRenameError("資料名に改行、タブ、制御文字は使用できません。");
              }}
              disabled={renameSaving}
              autoFocus
              className="mt-2 w-full max-w-full rounded-xl border border-[#D9C5B9] px-3 py-2 text-base text-[#4A3E39] outline-none focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6] disabled:opacity-60"
            />
            <p className="mt-2 text-xs font-bold text-[#8F7468]">255文字以内</p>
            {renameError ? (
              <p role="alert" className="mt-3 break-words rounded-xl bg-[#FFF0EB] px-3 py-2 text-sm font-bold text-[#B4533A] [overflow-wrap:anywhere]">
                {renameError}
              </p>
            ) : null}
            <div className="mt-5 flex flex-col-reverse flex-wrap gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setRenameTarget(null)}
                disabled={renameSaving}
                className="min-h-[42px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] disabled:opacity-60"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={renameSaving}
                className="min-h-[42px] rounded-full bg-[#5D4D46] px-4 py-2 text-sm font-black text-white disabled:opacity-60"
              >
                {renameSaving ? "保存中..." : "保存"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {reprocessTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#2F2521]/50 px-3 py-5 sm:px-4">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-reprocess-title"
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[1.4rem] bg-white p-5 shadow-xl"
          >
            <h2 id="knowledge-reprocess-title" className="break-words text-lg font-black text-[#5D4D46]">PDFをもう一度処理しますか？</h2>
            <p className="mt-3 break-words text-sm leading-6 text-[#6B5851] [overflow-wrap:anywhere]">
              「{getKnowledgeItemDisplayName(reprocessTarget)}」をもう一度処理します。現在の抽出結果と要約は作り直されます。
            </p>
            <div className="mt-5 flex flex-col-reverse flex-wrap gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setReprocessTarget(null)}
                disabled={reprocessStarting}
                className="min-h-[42px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] disabled:opacity-60"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => void reprocessPdf()}
                disabled={reprocessStarting}
                className="min-h-[42px] rounded-full bg-[#5D4D46] px-4 py-2 text-sm font-black text-white disabled:opacity-60"
              >
                {reprocessStarting ? "開始中..." : "もう一度処理"}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#2F2521]/50 px-3 py-5 sm:px-4">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-delete-title"
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[1.4rem] bg-white p-5 shadow-xl"
          >
            <h2 id="knowledge-delete-title" className="break-words text-lg font-black text-[#5D4D46]">
              {deleteTarget.type === "url" ? "このWebページをナレッジライブラリから削除しますか？" : "ナレッジライブラリから削除しますか？"}
            </h2>
            <p className="mt-3 break-words text-sm leading-6 text-[#6B5851] [overflow-wrap:anywhere]">
              {deleteTarget.type === "url"
                ? `「${getKnowledgeItemDisplayName(deleteTarget)}」をナレッジライブラリから削除します。この操作は取り消せません。`
                : deleteTarget.type === "deep_report"
                  ? `「${getKnowledgeItemDisplayName(deleteTarget)}」をナレッジライブラリから削除します。元の深掘りレポートは削除されません。`
                : deleteTarget.type === "company_research"
                  ? `「${getKnowledgeItemDisplayName(deleteTarget)}」をナレッジライブラリから削除します。元の企業リサーチ結果は削除されません。`
                  : `「${getKnowledgeItemDisplayName(deleteTarget)}」をナレッジライブラリから削除します。元の社長メモやPDFの元データは削除されません。`}
            </p>
            <div className="mt-5 flex flex-col-reverse flex-wrap gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deletePending}
                className="min-h-[42px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] disabled:opacity-60"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => void deleteKnowledge()}
                disabled={deletePending}
                className="min-h-[42px] rounded-full bg-[#A45645] px-4 py-2 text-sm font-black text-white disabled:opacity-60"
              >
                {deletePending ? "削除中..." : "ライブラリから削除"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
